/**
 * Theme Token Registry - Client-side mirror of storefront registry
 * 
 * Mirrors THEME_TOKENS_V1 from storefront for UI rendering.
 */

import type { ThemeTokenDefinition } from '../types';

// HSL component format (no hsl() wrapper) for Tailwind opacity support
// Format: "H S% L%" e.g. "222.2 47.4% 11.2%"
const HSL_PATTERN = /^\d{1,3}(\.\d+)?\s+\d{1,3}(\.\d+)?%\s+\d{1,3}(\.\d+)?%$/;

// Length pattern: number + unit (px, rem, em) or calc() expressions
const LENGTH_PATTERN = /^(\d+(\.\d+)?(px|rem|em)|calc\(.+\))$/;

export const THEME_TOKENS: ThemeTokenDefinition[] = [
  // Brand
  {
    key: 'color.primary',
    type: 'color',
    default: '222.2 47.4% 11.2%',
    cssVar: '--color-primary',
    alias: '--primary',
    group: 'brand',
    constraints: { pattern: HSL_PATTERN },
  },
  {
    key: 'color.primaryForeground',
    type: 'color',
    default: '210 40% 98%',
    cssVar: '--color-primary-foreground',
    alias: '--primary-foreground',
    group: 'brand',
    constraints: { pattern: HSL_PATTERN },
  },

  // Surfaces
  {
    key: 'color.background',
    type: 'color',
    default: '0 0% 100%',
    cssVar: '--color-background',
    alias: '--background',
    group: 'surfaces',
    constraints: { pattern: HSL_PATTERN },
  },
  {
    key: 'color.surface',
    type: 'color',
    default: '0 0% 100%',
    cssVar: '--color-surface',
    alias: '--card',
    group: 'surfaces',
    constraints: { pattern: HSL_PATTERN },
  },
  {
    key: 'color.text',
    type: 'color',
    default: '222.2 47.4% 11.2%',
    cssVar: '--color-text',
    alias: '--foreground',
    group: 'surfaces',
    constraints: { pattern: HSL_PATTERN },
  },
  {
    key: 'color.mutedText',
    type: 'color',
    default: '215.4 16.3% 46.9%',
    cssVar: '--color-muted-text',
    alias: '--muted-foreground',
    group: 'surfaces',
    constraints: { pattern: HSL_PATTERN },
  },
  {
    key: 'color.border',
    type: 'color',
    default: '214.3 31.8% 91.4%',
    cssVar: '--color-border',
    alias: '--border',
    group: 'surfaces',
    constraints: { pattern: HSL_PATTERN },
  },

  // Controls
  {
    key: 'radius.sm',
    type: 'length',
    default: 'calc(0.5rem - 4px)',
    cssVar: '--radius-sm',
    group: 'controls',
    constraints: { pattern: LENGTH_PATTERN },
  },
  {
    key: 'radius.md',
    type: 'length',
    default: 'calc(0.5rem - 2px)',
    cssVar: '--radius-md',
    group: 'controls',
    constraints: { pattern: LENGTH_PATTERN },
  },
  {
    key: 'radius.lg',
    type: 'length',
    default: '0.5rem',
    cssVar: '--radius-lg',
    alias: '--radius',
    group: 'controls',
    constraints: { pattern: LENGTH_PATTERN },
  },
] as const;

export const THEME_TOKEN_GROUPS = ['brand', 'surfaces', 'controls'] as const;

export function getTokensByGroup(group: string): ThemeTokenDefinition[] {
  return THEME_TOKENS.filter(t => t.group === group);
}
